/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { Store } from '@ngrx/store';
import { map, Observable, switchMap, throwError } from 'rxjs';
import { authenticationQuery } from '../../../shared/state/selectors/authentication/authentication.selectors';
import { CustomerDto } from '../interfaces/customer-dto.interface';
import { Customer } from '../interfaces/customer.interface';
import { CustomersDto } from '../interfaces/customers-dto.interface';
import { Customers } from '../interfaces/customers.interface';
import { GetCustomerParams } from '../interfaces/get-customers-params.interface';

@Injectable()
export class CustomerService {
  constructor(
    private _http: HttpClient,
    private _store: Store
  ) {}

  /**
   * Saves the customer and associates it with the supplier.
   * @param values The customer values to save.
   *
   * @returns An observable that emits the saved customer or an error.
   */
  createCustomer(values: CustomerDto): Observable<CustomerDto | Error> {
    if (!values) {
      return throwError(() => new Error('No values provided when trying to save customer'));
    }

    return this._store.select(authenticationQuery.getUser).pipe(
      switchMap(({ userId }) => {
        return this._http.post<CustomerDto>(`${environment.supplierApiGateway}/sup/account/${userId}/customers`, values, {
          headers: {
            'Content-Type': 'application/ate.organiser.v1+json'
          }
        });
      })
    );
  }

  /**
   * Gets all the customers associated with the supplier. Does not handle pagination yet,
   * so it will return all customers in one array.
   *
   * @param params Optional query parameters for filtering and pagination.
   *
   * @returns An observable that emits the listing customers or an error.
   */
  getCustomers(params?: GetCustomerParams): Observable<Customers> {
    return this._store.select(authenticationQuery.getUser).pipe(
      switchMap(({ userId }) => {
        return this._http
          .get<CustomersDto>(`${environment.supplierApiGateway}/sup/account/${userId}/customers`, {
            headers: {
              'Content-Type': 'application/ate.organiser.v1+json'
            },
            params: {
              ...params
            }
          })
          .pipe(
            map(response => {
              if (!response) {
                return { data: [] };
              }

              try {
                return {
                  ...response,
                  data: response.data.map((item: CustomerDto) => {
                    return {
                      ...item,
                      data: JSON.parse(item.data)
                    };
                  })
                };
              } catch (error) {
                console.error('Error parsing customer data:', error);
                return { data: [] };
              }
            })
          );
      })
    );
  }

  /**
   * Gets the specific listing customer for a the supplier.
   *
   * @returns An observable that emits the listing customer, null, or an error.
   */
  getCustomer(customerUuid: string): Observable<Customer | null> {
    if (!customerUuid) {
      return throwError(() => new Error('No customer UUID provided when trying to get customer'));
    }

    return this._store.select(authenticationQuery.getUser).pipe(
      switchMap(({ userId }) => {
        return this._http
          .get<CustomerDto>(`${environment.supplierApiGateway}/sup/account/${userId}/customers/${customerUuid}`, {
            headers: {
              'Content-Type': 'application/ate.organiser.v1+json'
            }
          })
          .pipe(
            map(response => {
              if (!response) {
                return null;
              }

              try {
                return { ...response, data: JSON.parse(response.data) };
              } catch (error) {
                console.error('Error parsing customer data:', error);
                return null;
              }
            })
          );
      })
    );
  }

  /**
   * Updates the customer with new values.
   *
   * @returns An observable that emits the listing customer, null, or an error.
   */
  updateCustomer(customerUuid: string, values: CustomerDto): Observable<Customer | null> {
    if (!customerUuid) {
      return throwError(() => new Error('No customer UUID provided when trying to update customer'));
    }

    return this._store.select(authenticationQuery.getUser).pipe(
      switchMap(({ userId }) => {
        return this._http
          .patch<CustomerDto>(`${environment.supplierApiGateway}/sup/account/${userId}/customers/${customerUuid}`, values, {
            headers: {
              'Content-Type': 'application/ate.organiser.v1+json'
            }
          })
          .pipe(
            map(response => {
              if (!response) {
                return null;
              }

              try {
                return { ...response, data: JSON.parse(response.data) };
              } catch (error) {
                console.error('Error parsing customer data:', error);
                return null;
              }
            })
          );
      })
    );
  }

  /**
   * Deletes a customer.
   *
   * @returns A success response or an error.
   */
  deleteCustomer(customerUuid: string): Observable<{
    success: boolean;
    uuid?: string;
  }> {
    if (!customerUuid) {
      return throwError(() => new Error('No customer UUID provided when trying to delete customer'));
    }

    return this._store.select(authenticationQuery.getUser).pipe(
      switchMap(({ userId }) => {
        return this._http.delete<{
          success: boolean;
          uuid?: string;
        }>(`${environment.supplierApiGateway}/sup/account/${userId}/customers/${customerUuid}`, {
          headers: {
            'Content-Type': 'application/ate.organiser.v1+json'
          }
        });
      })
    );
  }
}
